import React, { useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Star, Camera, X, Loader2, Gift, CheckCircle, Copy } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewForm = ({ garmentId, garmentName, onReviewSubmitted }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [discountCode, setDiscountCode] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    rating: 5,
    comment: '',
    order_id: ''
  });
  
  const [photos, setPhotos] = useState([]);
  const [photosPreviews, setPhotosPreviews] = useState([]);

  const handleRating = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 3) {
      toast({
        title: 'Máximo 3 fotos',
        description: 'Solo puedes subir hasta 3 fotos por reseña',
        variant: 'destructive'
      });
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Archivo muy grande',
          description: 'Cada foto debe ser menor a 5MB',
          variant: 'destructive'
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, e.target.result]);
        setPhotosPreviews(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotosPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.comment) {
      toast({
        title: 'Campos requeridos',
        description: 'Por favor completa tu nombre y comentario',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/reviews`, {
        garment_id: garmentId,
        customer_name: formData.customer_name,
        email: formData.email,
        rating: formData.rating,
        comment: formData.comment,
        photos: photos,
        order_id: formData.order_id || null
      });

      setDiscountCode(response.data.discount_code);
      setSubmitted(true);
      
      toast({
        title: '¡Gracias por tu reseña!',
        description: 'Tu código de descuento está listo',
      });

      if (onReviewSubmitted) {
        onReviewSubmitted(response.data);
      }

    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Error',
        description: 'No se pudo enviar la reseña. Inténtalo de nuevo.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(discountCode);
    toast({
      title: '¡Copiado!',
      description: 'Código copiado al portapapeles',
    });
  };

  if (submitted && discountCode) {
    return (
      <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">¡Gracias por tu reseña!</h3>
          <p className="text-gray-600 mb-4">
            Como agradecimiento, aquí tienes un código de descuento del <strong>2.22%</strong> para tu próxima compra:
          </p>
          
          <div className="bg-white rounded-xl p-4 border-2 border-dashed border-green-300 mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-pink-500" />
              <span className="text-sm text-gray-500">Tu código de descuento</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <span className="font-mono text-2xl font-bold text-purple-700">{discountCode}</span>
              <button
                onClick={copyCode}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                title="Copiar código"
              >
                <Copy className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-500">
            Válido durante 1 año • Un solo uso
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Star className="w-5 h-5 text-yellow-500" />
        Deja tu reseña de {garmentName}
      </h3>

      <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-pink-700 flex items-center gap-2">
          <Gift className="w-4 h-4" />
          <span>¡Obtén un <strong>2.22% de descuento</strong> en tu próxima compra al dejar una reseña!</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rating */}
        <div>
          <Label>Tu valoración</Label>
          <div className="flex gap-1 mt-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleRating(star)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={`w-8 h-8 ${
                    star <= formData.rating
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Name */}
        <div>
          <Label htmlFor="customer_name">Tu nombre *</Label>
          <Input
            id="customer_name"
            value={formData.customer_name}
            onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
            placeholder="María García"
            required
          />
        </div>

        {/* Email (optional) */}
        <div>
          <Label htmlFor="email">Email (opcional)</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="tu@email.com"
          />
          <p className="text-xs text-gray-500 mt-1">Para recibir el código por email</p>
        </div>

        {/* Order ID (optional) */}
        <div>
          <Label htmlFor="order_id">Número de pedido (opcional)</Label>
          <Input
            id="order_id"
            value={formData.order_id}
            onChange={(e) => setFormData(prev => ({ ...prev, order_id: e.target.value }))}
            placeholder="JRF-20250209-XXXXXXXX"
          />
          <p className="text-xs text-gray-500 mt-1">Para verificar tu compra y mostrar "Compra verificada"</p>
        </div>

        {/* Comment */}
        <div>
          <Label htmlFor="comment">Tu opinión *</Label>
          <Textarea
            id="comment"
            value={formData.comment}
            onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
            placeholder="Cuéntanos qué te ha parecido el producto..."
            rows={4}
            required
          />
        </div>

        {/* Photo Upload */}
        <div>
          <Label>Fotos (opcional, máx. 3)</Label>
          <div className="mt-2">
            {photosPreviews.length > 0 && (
              <div className="flex gap-2 mb-3 flex-wrap">
                {photosPreviews.map((preview, index) => (
                  <div key={index} className="relative">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {photos.length < 3 && (
              <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-pink-400 hover:bg-pink-50 transition">
                <Camera className="w-5 h-5 text-gray-500" />
                <span className="text-gray-600">Añadir fotos</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Star className="w-4 h-4 mr-2" />
              Enviar reseña y obtener descuento
            </>
          )}
        </Button>
      </form>
    </Card>
  );
};

export default ReviewForm;
